import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogoMark } from "./Logo";
import { PRELOADER } from "../constants/testIds";

const LETTERS = ["N", "E", "X", "X", "I", "S"];
const DURATION = 2400; // bar fill duration ms
const EXIT_DURATION = 800;

// Connection visual — a network of nodes progressively connecting as progress fills.
// Each node and line has a `t` (start progress 0..1).
const NODES = [
  { x: 40,  y: 70, t: 0.04 },
  { x: 110, y: 30, t: 0.10 },
  { x: 178, y: 78, t: 0.16 },
  { x: 246, y: 38, t: 0.22 },
  { x: 314, y: 86, t: 0.28 },
  { x: 382, y: 46, t: 0.34 },
  { x: 450, y: 70, t: 0.40 },
];

const LINES = [
  { from: 0, to: 1, t: 0.30, dur: 0.08 },
  { from: 1, to: 2, t: 0.36, dur: 0.08 },
  { from: 2, to: 3, t: 0.44, dur: 0.08 },
  { from: 3, to: 4, t: 0.52, dur: 0.08 },
  { from: 4, to: 5, t: 0.60, dur: 0.08 },
  { from: 5, to: 6, t: 0.68, dur: 0.08 },
  // diagonals (network feel)
  { from: 0, to: 2, t: 0.76, dur: 0.06 },
  { from: 2, to: 4, t: 0.82, dur: 0.06 },
  { from: 4, to: 6, t: 0.88, dur: 0.06 },
  { from: 1, to: 3, t: 0.92, dur: 0.04 },
  { from: 3, to: 5, t: 0.96, dur: 0.04 },
];

const lineLength = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

const ConnectionVisual = ({ progress }) => {
  return (
    <svg
      viewBox="0 0 490 116"
      width="100%"
      height="116"
      style={{ maxWidth: 460, display: "block" }}
      aria-hidden="true"
    >
      {/* Lines */}
      {LINES.map((ln, i) => {
        const a = NODES[ln.from];
        const b = NODES[ln.to];
        const len = lineLength(a, b);
        const fill = Math.max(0, Math.min(1, (progress - ln.t) / ln.dur));
        const dashOffset = len * (1 - fill);
        return (
          <line
            key={`l-${i}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke="#BEE5E0"
            strokeOpacity={0.55 * (fill > 0 ? 1 : 0)}
            strokeWidth={0.9}
            strokeDasharray={len}
            strokeDashoffset={dashOffset}
          />
        );
      })}

      {/* Nodes */}
      {NODES.map((n, i) => {
        const visible = progress >= n.t;
        const isCenter = i === 3; // middle node = coral hub
        const scale = visible ? (isCenter ? 1.2 : 1) : 0.2;
        const op = visible ? 1 : 0;
        return (
          <g key={`n-${i}`} style={{ transition: "opacity 280ms ease", opacity: op }}>
            {/* Glow halo */}
            {visible && (
              <circle
                cx={n.x}
                cy={n.y}
                r={isCenter ? 10 : 6}
                fill={isCenter ? "#FF6857" : "#BEE5E0"}
                opacity={isCenter ? 0.22 : 0.14}
              />
            )}
            <circle
              cx={n.x}
              cy={n.y}
              r={isCenter ? 4.5 : 3}
              fill={isCenter ? "#FF6857" : "#BEE5E0"}
              style={{
                transform: `scale(${scale})`,
                transformBox: "fill-box",
                transformOrigin: "center",
                transition: "transform 380ms cubic-bezier(0.22,1,0.36,1)",
              }}
            />
          </g>
        );
      })}
    </svg>
  );
};

export const Preloader = ({ onDone }) => {
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        // Small hold so the final connected state is seen, then exit
        setTimeout(() => setExiting(true), 220);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!exiting) return;
    const t = setTimeout(() => {
      onDone && onDone();
    }, EXIT_DURATION + 40);
    return () => clearTimeout(t);
  }, [exiting, onDone]);

  return (
    <motion.div
      key="preloader"
      data-testid={PRELOADER.root}
      className="fixed inset-0"
      style={{
        zIndex: 9999,
        background: "#0B4F4E",
        color: "#F7F6F2",
        pointerEvents: exiting ? "none" : "auto",
      }}
      initial={{ y: 0 }}
      animate={exiting ? { y: "-100%" } : { y: 0 }}
      transition={{ duration: EXIT_DURATION / 1000, ease: [0.76, 0, 0.24, 1] }}
    >
      {/* Solid teal atmosphere effects (inline so they don't depend on theme variables) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 12% 18%, rgba(190,229,224,0.16), transparent 65%), radial-gradient(50% 45% at 88% 82%, rgba(77,182,172,0.16), transparent 70%)",
        }}
      />
      <div className="relative z-10 mx-auto flex h-full max-w-[760px] flex-col items-center justify-center px-6">
        {/* NEXXIS "N" mark (white on the teal preloader) */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, scale: 0.82, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <LogoMark size={68} light />
        </motion.div>

        <div
          className="font-display flex items-baseline gap-[2px]"
          data-testid={PRELOADER.wordmark}
          aria-label="NEXXIS"
          style={{ fontWeight: 500, letterSpacing: "0.18em", fontSize: 32, color: "#F7F6F2" }}
        >
          {LETTERS.map((ch, i) => (
            <motion.span
              key={`${ch}-${i}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              {ch}
            </motion.span>
          ))}
        </div>

        <div
          className="kicker mt-3"
          style={{ color: "rgba(247,246,242,0.55)", letterSpacing: "0.32em" }}
        >
          LATAM — Global Markets
        </div>

        {/* Connection visual */}
        <motion.div
          className="mt-10 w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <ConnectionVisual progress={progress} />
        </motion.div>

        {/* Progress bar */}
        <div
          className="relative mt-10 h-[2px] w-[240px] sm:w-[280px] overflow-hidden"
          style={{ background: "rgba(247,246,242,0.18)" }}
        >
          <div
            data-testid={PRELOADER.progress}
            className="absolute inset-y-0 left-0"
            style={{
              width: `${Math.round(progress * 100)}%`,
              background: "#FF6857",
              transition: "width 90ms linear",
            }}
          />
        </div>
        <div
          className="font-ui mt-3 text-[11px] tabular-nums"
          style={{ color: "rgba(247,246,242,0.55)", letterSpacing: "0.18em" }}
        >
          {String(Math.round(progress * 100)).padStart(3, "0")}%  ·  CONNECTING
        </div>
      </div>
    </motion.div>
  );
};
